import React, { useState, useEffect } from "react"
import useTop10APage from "../Dashboarding.logic/useTop10APage"
import SimpleTable from "../Dashboarding.components/SimpleTable"
import { formatAmount } from "../Dashboarding.functions/formatAmount"
import "./Top10AView.css"
import RotateDeviceNotice from '../Dashboarding.components/RotateDeviceNotice'
import EmptyState from '../Dashboarding.components/EmptyState'

const periodFilters = ["FY", "YTD"]
const currencyOptions = ["C€", "€", "£", "$", "C$"]
const limitOptions = [10, 25, 50, 100, 1000]

const Top10AView = () => {
  const [selectedEntity, setSelectedEntity] = useState("WGE")
  const [selectedCurrency, setSelectedCurrency] = useState("C€")
  const [selectedPeriod, setSelectedPeriod] = useState("YTD")
  const [selectedProducts, setSelectedProducts] = useState([])
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedClients, setSelectedClients] = useState([])
  const [limit, setLimit] = useState(10)
  const [availableEntities, setAvailableEntities] = useState(["WGALL", "WGE", "WGUK"])

  const filters = {
    entity: selectedEntity,
    currency: selectedCurrency,
    period: selectedPeriod,
    products: selectedProducts,
    countries: selectedCountries,
    clients: selectedClients,
    limit,
  }

  const {
    topCountries,
    topClients,
    topProducts,
    topProductsBySales,
    THIS_YEAR,
    LAST_YEAR,
    isLoading,
    metadata,
  } = useTop10APage(filters)

  useEffect(() => {
    if (metadata?.availableEntities) {
      setAvailableEntities(metadata.availableEntities)
      if (!metadata.availableEntities.includes(selectedEntity)) {
        setSelectedEntity(metadata.availableEntities[0])
      }
    }
  }, [metadata, selectedEntity])

  if (isLoading) return <div>Chargement...</div>

  // Check if we have no data at all
  const hasNoData = topCountries.length === 0 && topClients.length === 0 && topProducts.length === 0 && topProductsBySales.length === 0

  console.log('Received data lengths:', {
    countries: topCountries.length,
    clients: topClients.length,
    products: topProducts.length,
    productsBySales: topProductsBySales.length,
  })

  const maxCountries = Math.max(...topCountries.map(c => c[THIS_YEAR] || 0), 1)
  const maxClients = Math.max(...topClients.map(c => c[THIS_YEAR] || 0), 1)
  const maxProducts = Math.max(...topProducts.map(p => p[THIS_YEAR] || 0), 1)
  const maxProductsSales = Math.max(...topProductsBySales.map(p => p[THIS_YEAR] || 0), 1)

  const formatNumber = (num) => {
    if (num === null || num === undefined) return num
    return formatAmount(num)
  }

  const renderBar = (value, max, isThisYear = true) => {
    const percentage = Math.max(5, (value / max) * 100)
    return (
      <div className="bar-cell">
        <div className="progress-bar-container">
          <div
            className={`progress-bar ${isThisYear ? 'this-year' : 'last-year'}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="bar-text">
          {formatNumber(value)}
        </span>
      </div>
    )
  }

  const renderVariation = (value) => (
    <span className={value < 0 ? 'dashboard-table-cell-varneg' : 'dashboard-table-cell-varpos'}>
      {value !== null && value !== undefined ? `${value.toFixed(2)} %` : ''}
    </span>
  )

  const countriesColumns = [
    { key: 'country', label: 'Country', align: 'left' },
    { key: THIS_YEAR, label: 'This Year', render: (val) => renderBar(val, maxCountries, true) },
    { key: LAST_YEAR, label: 'Last Year', render: (val) => renderBar(val, maxCountries, false) },
    { key: 'varPct', label: 'Var %', render: renderVariation },
  ]

  const clientsColumns = [
    { key: 'client', label: 'Client', align: 'left' },
    { key: THIS_YEAR, label: 'This Year', render: (val) => renderBar(val, maxClients, true) },
    { key: LAST_YEAR, label: 'Last Year', render: (val) => renderBar(val, maxClients, false) },
    { key: 'varPct', label: 'Var %', render: renderVariation },
  ]

  const productsColumns = [
    { key: 'product', label: 'Product', align: 'left' },
    {
      key: 'image',
      label: '',
      render: (url, row) => (url ? <img src={url} alt={row.product} className="dashboard-product-img" /> : null),
    },
    { key: THIS_YEAR, label: 'This Year', render: (val) => renderBar(val, maxProducts, true) },
    { key: LAST_YEAR, label: 'Last Year', render: (val) => renderBar(val, maxProducts, false) },
    { key: 'varPct', label: 'Var %', render: renderVariation },
  ]

  const productsBySalesColumns = [
    { key: 'product', label: 'Product', align: 'left' },
    {
      key: 'image',
      label: '',
      render: (url, row) => (url ? <img src={url} alt={row.product} className="dashboard-product-img" /> : null),
    },
    { key: THIS_YEAR, label: 'This Year', render: (val) => renderBar(val, maxProductsSales, true) },
    { key: LAST_YEAR, label: 'Last Year', render: (val) => renderBar(val, maxProductsSales, false) },
    { key: 'varPct', label: 'Var %', render: renderVariation },
  ]

  const handleProductRowClick = (row) => {
    const productName = row.product
    setSelectedProducts((prev) => (
      prev.includes(productName) ? prev.filter(p => p !== productName) : [...prev, productName]
    ))
  }

  const handleCountryRowClick = (row) => {
    const countryName = row.country
    setSelectedCountries((prev) => (
      prev.includes(countryName) ? prev.filter(c => c !== countryName) : [...prev, countryName]
    ))
  }

  const handleClientRowClick = (row) => {
    const clientName = row.client
    setSelectedClients((prev) => (
      prev.includes(clientName) ? prev.filter(c => c !== clientName) : [...prev, clientName]
    ))
  }

  const getProductRowClass = (row) => (selectedProducts.includes(row.product) ? 'dashboard-product-row-selected' : '')
  const getCountryRowClass = (row) => (selectedCountries.includes(row.country) ? 'dashboard-country-row-selected' : '')
  const getClientRowClass = (row) => (selectedClients.includes(row.client) ? 'dashboard-client-row-selected' : '')

  return (
    <>
      <RotateDeviceNotice />
      <div className="dashboard-top10a-root">
        <div className="top10a-header">
          <div className="top10a-header-filters">
            <div className="top10a-filter-group">
              <span className="top10a-filter-label">Entity:</span>
              {availableEntities.map((e) => (
                <button
                  type="button"
                  className={`top10a-filter-btn ${selectedEntity === e ? 'active' : ''}`}
                  key={e}
                  onClick={() => setSelectedEntity(e)}
                >
                  {e}
                </button>
              ))}
            </div>

            <div className="top10a-filter-group">
              <span className="top10a-filter-label">Period:</span>
              {periodFilters.map((p) => (
                <button
                  type="button"
                  className={`top10a-filter-btn ${selectedPeriod === p ? 'active' : ''}`}
                  key={p}
                  onClick={() => setSelectedPeriod(p)}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="top10a-filter-group">
              <span className="top10a-filter-label">Currency:</span>
              {currencyOptions.map((c) => (
                <button
                  type="button"
                  className={`top10a-filter-btn ${selectedCurrency === c ? 'active' : ''}`}
                  key={c}
                  onClick={() => setSelectedCurrency(c)}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="top10a-filter-group">
              <span className="top10a-filter-label">Show:</span>
              <select
                className="top10a-filter-select"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {limitOptions.map((l) => (
                  <option key={l} value={l}>
                    Top
                    {' '}
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {hasNoData ? (
            <div style={{ gridColumn: '1 / -1', padding: '2rem' }}>
              <EmptyState
                title="No data available"
                description="No data found for the selected filters. Try adjusting your entity, period, or other filters."
                icon="📈"
              />
            </div>
          ) : (
            <>
              <div className="dashboard-card">
                <div className="dashboard-card-header">
                  <h3 className="dashboard-card-title">
                    Top
                    {' '}
                    {limit}
                    {' '}
                    Countries
                  </h3>
                  {selectedCountries.length > 0 && (
                    <button
                      type="button"
                      className="dashboard-clear-btn"
                      onClick={() => setSelectedCountries([])}
                    >
                      Clear (
                      {selectedCountries.length}
                      )
                    </button>
                  )}
                </div>
                <div className="dashboard-card-content">
                  <div className="dashboard-table-wrapper">
                    <SimpleTable
                      columns={countriesColumns}
                      data={topCountries}
                      onRowClick={handleCountryRowClick}
                      getRowClass={getCountryRowClass}
                      showTotal
                    />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="dashboard-card-header">
                  <h3 className="dashboard-card-title">
                    Top
                    {' '}
                    {limit}
                    {' '}
                    Clients
                  </h3>
                  {selectedClients.length > 0 && (
                    <button
                      type="button"
                      className="dashboard-clear-btn"
                      onClick={() => setSelectedClients([])}
                    >
                      Clear (
                      {selectedClients.length}
                      )
                    </button>
                  )}
                </div>
                <div className="dashboard-card-content">
                  <div className="dashboard-table-wrapper">
                    <SimpleTable
                      columns={clientsColumns}
                      data={topClients}
                      onRowClick={handleClientRowClick}
                      getRowClass={getClientRowClass}
                      showTotal
                    />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="dashboard-card-header">
                  <h3 className="dashboard-card-title">
                    Top
                    {' '}
                    {limit}
                    {' '}
                    Products by Sales
                  </h3>
                  {selectedProducts.length > 0 && (
                    <button
                      type="button"
                      className="dashboard-clear-btn"
                      onClick={() => setSelectedProducts([])}
                    >
                      Clear (
                      {selectedProducts.length}
                      )
                    </button>
                  )}
                </div>
                <div className="dashboard-card-content">
                  <div className="dashboard-table-wrapper">
                    <SimpleTable
                      columns={productsBySalesColumns}
                      data={topProductsBySales}
                      onRowClick={handleProductRowClick}
                      getRowClass={getProductRowClass}
                      showTotal
                    />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="dashboard-card-header">
                  <h3 className="dashboard-card-title">
                    Top
                    {' '}
                    {limit}
                    {' '}
                    Products by Quantities
                  </h3>
                  {selectedProducts.length > 0 && (
                    <button
                      type="button"
                      className="dashboard-clear-btn"
                      onClick={() => setSelectedProducts([])}
                    >
                      Clear (
                      {selectedProducts.length}
                      )
                    </button>
                  )}
                </div>
                <div className="dashboard-card-content">
                  <div className="dashboard-table-wrapper">
                    <SimpleTable
                      columns={productsColumns}
                      data={topProducts}
                      onRowClick={handleProductRowClick}
                      getRowClass={getProductRowClass}
                      showTotal
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Top10AView